/* ---------------------------------------------------------------------------
   The handover, on the site record (LIFECYCLE.md `LC-11`).

   One dialog, opened from Monitoring's ribbon. A resident engineer
   (`handover.prepare`) fills it and prepares it; an approver
   (`handover.accept`) accepts it or returns it; once accepted it is the
   record, read-only, and the bridge is in service.

   THE FORM IS READ WHEN IT IS SUBMITTED, as the decision form on the bridge
   record is, so typing costs no render. The source of each inventory value —
   the model's, or edited — is the server's to derive on every save; the form
   shows what came back.

   THE REFUSALS THE ENDPOINT MAKES ARE SAID ON THE BUTTON: whoever prepared it
   cannot accept it, and a hold point or NCR open without a waiver holds it.

   WITH WORKS OPEN ON A BRIDGE IN SERVICE the same page is their completion
   (LC-82): the same record and the same two people, a model only where the
   works brought one, and a special inspection due where the handover's
   baseline would be. Accepting it closes the works; the phase stays.
   --------------------------------------------------------------------------- */

import { h, type Child } from '../core/dom.ts';
import { actions, can, type State } from '../core/store.ts';
import type { HandoverView } from '../core/api.ts';
import { button, emptyState, fact, field, scrim, tag } from '../components/widgets.ts';
import { findBridge, prettyDate } from '../data/derive.ts';
import { DEFECT_CODES, DEFECT_SEVERITIES } from '../data/defects.ts';
import {
  HANDOVER_FIELDS,
  HANDOVER_STATUS_LABEL,
  dlpEnd,
  unwaived,
  type FieldValue,
  type Handover,
  type HandoverFieldKey,
  type HandoverStatus,
} from '../data/handover.ts';
import { WORKS_KIND_LABEL, phaseOf } from '../data/phase.ts';
import { normaliseIdentity } from '../data/roles.ts';
import { join } from '../data/recorded.ts';

const STATUS_RANK: Readonly<Record<HandoverStatus, 1 | 2 | 3>> = { draft: 2, prepared: 3, accepted: 1 };

/** The handover's status as a chip: prepared is the one waiting on somebody. */
export function handoverChip(status: HandoverStatus | null): HTMLElement {
  return status === null ? tag('Not started', 'none') : tag(HANDOVER_STATUS_LABEL[status], STATUS_RANK[status]);
}

function shownValue(v: FieldValue, unit?: string): string {
  if (v === null || v === '') return '—';
  return unit ? `${v} ${unit}` : String(v);
}

/** The inventory the handover fills, each value beside where it came from. */
function inventoryTable(h0: Handover | null, editable: boolean): HTMLElement {
  return h(
    'table',
    { class: 'tbl' },
    h(
      'thead',
      {},
      h('tr', {}, h('th', { style: 'padding-left:14px', text: 'Field' }), h('th', { text: 'Value' }), h('th', { text: 'Source' })),
    ),
    h(
      'tbody',
      {},
      ...HANDOVER_FIELDS.map((f) => {
        const entry = h0?.inventory[f.key];
        const value = entry?.value ?? null;
        return h(
          'tr',
          { 'data-field': f.key },
          h('td', { style: 'padding-left:14px', text: f.unit ? `${f.label} (${f.unit})` : f.label }),
          h(
            'td',
            {},
            editable
              ? h('input', {
                  class: 'input',
                  name: `inv:${f.key}`,
                  type: f.kind === 'text' ? 'text' : 'number',
                  ...(f.kind === 'text' ? {} : { step: f.kind === 'int' ? '1' : 'any', min: String(f.min ?? 0) }),
                  value: value === null ? '' : String(value),
                  'aria-label': f.label,
                })
              : shownValue(value, f.unit),
          ),
          h(
            'td',
            { 'data-source': entry?.source ?? '' },
            entry === undefined
              ? '—'
              : entry.source === 'model'
                ? tag('Model', 1, 'As the as-built model states it')
                : tag('Edited', 2, entry.modelValue === null ? 'The model does not state it' : `The model says ${shownValue(entry.modelValue, f.unit)}`),
          ),
        );
      }),
    ),
  );
}

/** The open NCRs and incidents, each with the defect it will become. */
function carryTable(view: HandoverView, editable: boolean): Child {
  if (view.carry.length === 0) return null;
  const select = (name: string, value: string | null, options: readonly { value: string; text: string }[], label: string) =>
    h(
      'select',
      { class: 'select', name, 'aria-label': label },
      h('option', { value: '', selected: value === null, text: 'Choose…' }),
      ...options.map((o) => h('option', { value: o.value, selected: o.value === value, text: o.text })),
    );
  return h(
    'div',
    { class: 'works__sub' },
    h('div', { class: 'works__sub-head', text: 'Carried over as issues' }),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Ref' }),
          h('th', { text: 'What' }),
          h('th', { text: 'Category' }),
          h('th', { text: 'Severity' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...view.carry.map((c) =>
          h(
            'tr',
            { 'data-carry': c.ref },
            h('td', { style: 'padding-left:14px', class: 'display', text: c.ref }),
            h('td', { text: c.title }),
            h(
              'td',
              {},
              editable
                ? select(`code:${c.id}`, c.code, DEFECT_CODES.map((d) => ({ value: d.code, text: d.label })), `Category for ${c.ref}`)
                : (DEFECT_CODES.find((d) => d.code === c.code)?.label ?? '—'),
            ),
            h(
              'td',
              {},
              editable
                ? select(`sev:${c.id}`, c.severity, DEFECT_SEVERITIES.map((v) => ({ value: v, text: v })), `Severity for ${c.ref}`)
                : join([c.severity, c.defectId ? `issue ${c.defectId.slice(0, 8)}` : null], ' · ') || '—',
            ),
          ),
        ),
      ),
    ),
  );
}

/** The hold points and NCRs still open, each with its waiver. */
function blockingTable(view: HandoverView, editable: boolean): Child {
  if (view.blocking.length === 0) return null;
  const waivers = view.handover?.waivers ?? [];
  return h(
    'div',
    { class: 'works__sub' },
    h('div', { class: 'works__sub-head', text: 'Open hold points and NCRs' }),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h('tr', {}, h('th', { style: 'padding-left:14px', text: 'Ref' }), h('th', { text: 'Title' }), h('th', { text: 'Waiver' })),
      ),
      h(
        'tbody',
        {},
        ...view.blocking.map((i) => {
          const w = waivers.find((x) => x.itemId === i.id) ?? null;
          return h(
            'tr',
            { 'data-blocking': i.ref, 'data-waived': w ? 'yes' : 'no' },
            h('td', { style: 'padding-left:14px', class: 'display', text: i.ref }),
            h('td', { text: i.title }),
            h(
              'td',
              {},
              editable
                ? h('input', {
                    class: 'input',
                    name: `waive:${i.id}`,
                    type: 'text',
                    value: w?.reason ?? '',
                    placeholder: 'Reason to accept it open',
                    'aria-label': `Waiver for ${i.ref}`,
                  })
                : w
                  ? `${w.reason}${w.by ? ` — ${w.by}` : ''}`
                  : tag('No waiver', 3),
            ),
          );
        }),
      ),
    ),
  );
}

/** What the form holds, as the endpoint reads it. */
function readForm(form: HTMLFormElement, view: HandoverView, prepare: boolean): Parameters<typeof actions.saveHandover>[0] {
  const data = new FormData(form);
  const text = (name: string): string => String(data.get(name) ?? '').trim();
  const inventory: Partial<Record<HandoverFieldKey, FieldValue>> = {};
  for (const f of HANDOVER_FIELDS) {
    const raw = text(`inv:${f.key}`);
    inventory[f.key] = raw === '' ? null : f.kind === 'text' ? raw : Number(raw);
  }
  const months = text('dlpMonths');
  return {
    completedOn: text('completedOn') || null,
    dlpMonths: months === '' ? null : Number(months),
    modelId: text('modelId') || null,
    inventory,
    carry: view.carry.map((c) => ({ id: c.id, code: text(`code:${c.id}`) || null, severity: text(`sev:${c.id}`) || null })),
    waivers: view.blocking.map((i) => ({ itemId: i.id, reason: text(`waive:${i.id}`) })),
    note: text('note'),
    prepare,
  };
}

/**
 * The dialog: the handover as it stands, and the act open to whoever is
 * looking. Opened from Monitoring's ribbon; null while closed.
 */
export function handoverDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.handoverOpen) return null;
  const b = findBridge(s.selectedId);
  if (b === null) return null;
  const close = () => actions.closeHandover();
  const view = s.handover;

  const ho = view?.handover ?? null;
  const status = ho?.status ?? null;
  const p = phaseOf(b);
  /* The works completion, where works are open (LC-82); the handover otherwise. */
  const works = p.works !== null && view?.kind === 'works';
  const title = works ? 'Works completion' : 'Handover';
  const underConstruction = p.phase === 'construction' || works;
  const mayPrepare = can(s, 'handover.prepare') && underConstruction && status !== 'accepted' && s.backend === 'live';
  const mayAccept = can(s, 'handover.accept') && status === 'prepared' && s.backend === 'live';
  const me = s.identity?.author ?? null;
  const own = me !== null && ho?.preparedBy != null && normaliseIdentity(me) === normaliseIdentity(ho.preparedBy);

  const sheet = (body: Child[], foot: Child[]): HTMLElement =>
    h(
      'div',
      { class: 'dlg', ...scrim(close, () => s.handoverBusy) },
      h(
        'div',
        { class: 'dlg__panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': `${title} — ${b.name}` },
        h(
          'div',
          { class: 'dlg__head' },
          h(
            'div',
            {},
            h('div', { class: 'dlg__kicker', text: `${b.id} · ${works ? 'works' : 'handover'}` }),
            h('h2', { class: 'dlg__title', text: title }),
          ),
          h('span', { class: 'spacer' }),
          view === null ? null : handoverChip(status),
          h('button', { type: 'button', class: 'dlg__close', title: 'Close', 'aria-label': 'Close', onclick: close }, '×'),
        ),
        h('div', { class: 'dlg__body' }, ...body),
        h('div', { class: 'dlg__foot' }, h('span', { class: 'spacer' }), ...foot),
      ),
    );
  const closeButton = (label: string) => button(label, { onClick: close, disabled: s.handoverBusy });

  if (view === null) return sheet([emptyState('The handover has not been read yet.')], [closeButton('Close')]);

  const held = ho ? unwaived(view.blocking, ho.waivers) : view.blocking;

  if (ho === null && !mayPrepare) {
    return sheet(
      [
        emptyState(
          works
            ? 'No completion has been started for these works.'
            : underConstruction
              ? 'No handover has been started for this structure.'
              : 'This structure was not handed over through this register.',
        ),
      ],
      [closeButton('Close')],
    );
  }

  const facts = h(
    'div',
    { class: 'det-facts' },
    works ? fact('Works', WORKS_KIND_LABEL[p.works!]) : null,
    fact(works ? 'Completion' : 'Completion (taking-over)', ho?.completedOn ? prettyDate(ho.completedOn) : '—'),
    fact('Defects liability', ho?.dlpMonths != null ? join([`${ho.dlpMonths} months`, ho.dlpEndsOn ? `until ${prettyDate(ho.dlpEndsOn)}` : null], ' · ') : '—'),
    fact('As-built model', ho?.modelId ? (s.models.models.find((m) => m.id === ho.modelId)?.name ?? ho.modelId) : '—'),
    works
      ? fact('Special inspection due', ho?.completedOn ? prettyDate(ho.completedOn) : '—')
      : fact('Baseline inspection due', ho?.baselineDueOn ? prettyDate(ho.baselineDueOn) : '—'),
    fact('Prepared', ho?.preparedAt ? join([prettyDate(ho.preparedAt.slice(0, 10)), ho.preparedBy], ' · ') : '—'),
    fact('Accepted', ho?.acceptedAt ? join([prettyDate(ho.acceptedAt.slice(0, 10)), ho.acceptedBy], ' · ') : '—'),
  );

  const notes = ho?.modelNotes.length ? h('div', { class: 'muted', text: ho.modelNotes.join(' ') }) : null;
  const error = s.handoverError ? h('div', { class: 'dlg__note dlg__note--error', text: s.handoverError }) : null;

  const acceptControls = mayAccept
    ? h(
        'form',
        {
          class: 'det-state__decide',
          onsubmit: (e: Event) => {
            e.preventDefault();
            const reason = ((e.currentTarget as HTMLFormElement).querySelector('input') as HTMLInputElement).value;
            void actions.actOnHandover('return', reason);
          },
        },
        button(works ? 'Accept completion' : 'Accept handover', {
          sm: true,
          primary: true,
          disabled: own || held.length > 0 || s.handoverBusy,
          title: own
            ? `You prepared this ${works ? 'completion' : 'handover'}, so somebody else must accept it`
            : held.length
              ? `${held.map((i) => i.ref).join(', ')} open with no waiver`
              : works
                ? 'Accept the completion: the works close, and a special inspection falls due'
                : 'Accept the handover: the bridge goes into service',
          onClick: () => void actions.actOnHandover('accept'),
        }),
        h('input', { class: 'input', type: 'text', placeholder: 'Reason to return it', 'aria-label': 'Reason to return it' }),
        button('Return', { sm: true, type: 'submit', disabled: s.handoverBusy }),
      )
    : null;

  if (!mayPrepare) {
    return sheet(
      [
        error,
        facts,
        inventoryTable(ho, false),
        notes,
        carryTable(view, false),
        blockingTable(view, false),
        ho?.note ? h('div', { class: 'muted', text: ho.note }) : null,
        acceptControls,
      ],
      [closeButton('Close')],
    );
  }

  const models = s.models.models;
  const form = h(
    'form',
    {
      id: 'handover-form',
      onsubmit: (e: Event) => {
        e.preventDefault();
        const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
        void actions.saveHandover(readForm(e.currentTarget as HTMLFormElement, view, submitter?.value === 'prepare'));
      },
    },
    h(
      'div',
      { class: 'dlg__form-grid' },
      field(
        works ? 'Completion date' : 'Completion (taking-over) date',
        h('input', { id: 'ho-completed', class: 'input', type: 'date', name: 'completedOn', value: ho?.completedOn ?? '' }),
        'ho-completed',
      ),
      field(
        ho?.completedOn && ho.dlpMonths != null ? `Defects liability, months — until ${prettyDate(dlpEnd(ho.completedOn, ho.dlpMonths))}` : 'Defects liability, months',
        h('input', { id: 'ho-dlp', class: 'input', type: 'number', min: '0', max: '120', step: '1', name: 'dlpMonths', value: ho?.dlpMonths != null ? String(ho.dlpMonths) : '' }),
        'ho-dlp',
      ),
      field(
        'As-built model',
        h(
          'select',
          { id: 'ho-model', class: 'select', name: 'modelId' },
          h('option', {
            value: '',
            selected: !ho?.modelId,
            text: works ? 'None: the works change no model' : models.length ? 'Choose…' : 'No model on this structure',
          }),
          ...models.map((m) => h('option', { value: m.id, selected: m.id === ho?.modelId, text: m.name || m.id })),
        ),
        'ho-model',
      ),
    ),
    inventoryTable(ho, true),
    notes,
    carryTable(view, true),
    blockingTable(view, true),
    field('Note', h('textarea', { id: 'ho-note', class: 'input', rows: 2, name: 'note', value: ho?.note ?? '' }), 'ho-note'),
  );

  /* The foot's buttons submit the form by its id: the acceptance is a form of
     its own, and forms do not nest. */
  return sheet(
    [error, facts, form, acceptControls],
    [
      closeButton('Cancel'),
      h('button', { type: 'submit', form: 'handover-form', class: 'btn', disabled: s.handoverBusy || undefined }, 'Save draft'),
      h(
        'button',
        {
          type: 'submit',
          form: 'handover-form',
          value: 'prepare',
          class: ['btn', 'btn--primary'],
          disabled: s.handoverBusy || undefined,
          title: 'Prepared: ready for an approver to accept',
        },
        'Prepare',
      ),
    ],
  );
}
